// src/utils/cvDocument.js — EST-CE UN CV ? La règle est écrite ICI, une fois.
//
// Vécu 11.09 : « pourquoi Karina, quand j'affiche son CV je ne peux pas
// l'éditer, alors que sur un autre candidat je peux ? » — vue besoin, éditeur
// en ligne.
//
// Ce n'était pas le candidat, c'était le DOCUMENT. Le bouton « Éditer » de
// l'aperçu était réservé aux CV PRODUITS par la préparation (source
// `generation` / `cv_prep`, `kind: generated`) ; un CV DÉPOSÉ ne l'obtenait
// jamais. Karina n'avait pas encore de CV format entreprise — donc pas de
// bouton, et rien pour le dire.
//
// Cette réserve n'a plus lieu d'être : quand aucune préparation n'existe, le
// lanceur (CvEditeurLauncher) en crée une par reprise fidèle du contenu du
// fichier (`mode: 'import'`), comme on ouvre un document dans Word. L'aperçu
// cachait donc une capacité qui existe — le pire des états : rien à cliquer,
// et aucune explication.
//
// CE QUI RESTE VRAI : l'éditeur de CV n'a de sens que sur un CV. Une matrice
// de compétences est elle aussi un document généré, mais elle se refait par
// « Matrice », jamais par l'éditeur de CV.
//
// POURQUOI UN MODULE. Chaque écran reconnaissait « un CV » à sa façon (`gen`,
// `generatedPrep`, `kind`, le libellé…) et deux appels du visualiseur ne le
// reconnaissaient pas du tout (`cv={false}` en dur). Une règle écrite six fois
// est une règle qui diverge à six endroits — c'est exactement ce qui est
// arrivé. Un nouvel écran appelle `estCvDocument` et hérite de la règle.

/** Les formes de champ que les différents écrans transportent pour un même
 *  document (liste candidate_documents, document de positionnement, rangée de
 *  repli du CV de fiche, résultat de matching). */
const texteDe = (doc) => [
  doc.label, doc.originalName, doc.original_name, doc.name, doc.fileName, doc.file_name,
  doc.typePiece, doc.type_piece, doc.type,
].filter(Boolean).join(' ');

/**
 * Fabriqué par l'ERP (par opposition à déposé par quelqu'un).
 *
 * ⚠️ Ça dit QUI a fabriqué le fichier, PAS ce que c'est. La matrice de
 * compétences, le NDA signé, le contrat sont « générés » eux aussi. Ne jamais
 * s'en servir pour deviner la NATURE d'un document — c'est exactement l'erreur
 * qui a mis un NDA dans la section CV (vécu 15.09).
 */
export const estDocumentGenere = (doc) => !!doc && (
  doc.generatedPrep === true || doc.gen === true
  || doc.kind === 'generated' || doc.source === 'generation' || doc.source === 'cv_prep');

/**
 * Produit par la PRÉPARATION DE CV. Celui-là, contrairement à « généré »,
 * ne fabrique que des CV : c'est le seul atelier dont la sortie est un CV par
 * définition.
 */
export const estCvProduit = (doc) => !!doc && doc.source === 'cv_prep';

/** Le mot « CV », isolé — pas « cvthèque », pas « recv ». */
const DIT_CV = /(^|[^a-zA-Z])cv([^a-zA-Z]|$)/i;

/**
 * Ce document est-il un CV — déposé ou produit par le générateur ?
 *
 * ON RECONNAÎT UN CV À CE QU'IL EST, pas à qui l'a fabriqué. La règle a
 * longtemps été « c'est généré, donc c'est un CV, sauf si c'est une matrice » :
 * une liste d'exceptions, qu'il aurait fallu allonger à chaque nouveau
 * document produit par l'ERP. Le NDA signé est arrivé, personne n'y a pensé,
 * et il s'est rangé dans la section CV avec la pastille « format entreprise »
 * (vécu 15.09 : « pourquoi on a un NDA dans la section CV ? »). La question
 * n'est pas « d'où vient ce fichier » mais « est-ce un CV ».
 *
 * `estCv` permet à l'écran de trancher quand il le SAIT mieux qu'un nom de
 * fichier : la rangée « CV de la fiche », le CV désigné pour un besoin ou le
 * sélecteur de CV d'un dossier AO restent des CV même nommés
 * « Karina_2024.pdf ».
 */
export const estCvDocument = (doc) => {
  if (!doc) return false;
  if (doc.estCv === true) return true;
  if (doc.estCv === false) return false;
  return estCvProduit(doc) || DIT_CV.test(texteDe(doc));
};

/** Un livrable produit par l'ERP (CV format entreprise, contrat) — jamais une
 *  matière première. Repli texte pour les fichiers anciens sans `source`. */
const DIT_LIVRABLE = /format[ _]?entreprise|cv[_ ]?tat|\bcontrat\b|matrice/i;

const quandDe = (doc) => Date.parse(doc.updated_at || doc.updatedAt || doc.created_at || doc.createdAt || doc.uploaded_at || '') || 0;

/**
 * Les CV d'une fiche qui peuvent SERVIR DE SOURCE au CV format entreprise.
 *
 * Vécu 02.10 : « le candidat a un CV mais quand je fais CV entreprise il ne
 * le trouve pas ». Depuis la fiche, l'écran passait au générateur l'adresse
 * du CV ; depuis le besoin, il ne passait rien — le générateur ne savait lire
 * que `cv_url`, et demandait un fichier qu'on venait de voir dans la liste.
 *
 * Un CV déposé (pas produit par l'ERP), de la fiche ou de CE besoin — jamais
 * celui d'un autre besoin. Dans l'ordre : celui rattaché à ce besoin, puis le
 * CV principal de la fiche, puis le plus récent.
 */
export const cvsSources = (docs, { besoinId = null } = {}) => (Array.isArray(docs) ? docs : [])
  .filter(d => d && d.id != null && estCvDocument(d) && !estDocumentGenere(d) && !DIT_LIVRABLE.test(texteDe(d)))
  .filter(d => { const b = d.besoin_id ?? d.besoinId ?? null; return b == null || (besoinId != null && String(b) === String(besoinId)); })
  .map(d => ({
    id: d.id,
    nom: d.label || d.name || d.original_name || d.originalName || 'CV',
    fichier: d.original_name || d.originalName || null,
    url: d.url || d.cv_url || null,
    ceBesoin: (d.besoin_id ?? d.besoinId) != null,
    principal: d.is_principal === true,
    quand: quandDe(d),
  }))
  .sort((a, b) => (Number(b.ceBesoin) - Number(a.ceBesoin)) || (Number(b.principal) - Number(a.principal)) || (b.quand - a.quand));

export default estCvDocument;
